/**
 * The level map's scenery: the ten worlds of the climb, stacked bottom to top.
 *
 * Each world (ten levels) gets its own sky, a far ridge, near ground and the
 * things that make it that world - the sea and a penguin colony at Sunset
 * Shore, aurora over the plains, huts at the research station, the ceremonial
 * South Pole at the top. A band of cloud hides each seam, so climbing reads as
 * rising through the clouds into the next world.
 *
 * Everything is seeded per world, so the map looks the same on every visit, and
 * each world is one Graphics that is hidden while it is off screen.
 */
import Phaser from 'phaser';
import { mulberry32 } from '../../systems/rng';
import type { Rng } from '../../systems/rng';
import { paletteFor, THEME_BANDS } from '../../data/themes';
import type { EnvironmentPalette, GradientStops } from '../../data/themes';
import { regionSpan, summitPoint } from '../../systems/levelMapPath';
import type { MapNode } from '../../systems/levelMapPath';
import { GAME_WIDTH } from '../../types';
import type { BackgroundTheme } from '../../types';

type Gfx = Phaser.GameObjects.Graphics;

const W = GAME_WIDTH;
/** Scenery keeps this far from the trail, so nodes never sit on clutter. */
const TRAIL_CLEARANCE = 120;

export interface MapScenery {
  /** Show only the worlds that overlap the camera's view (plus a margin). */
  cull(viewTop: number, viewBottom: number): void;
}

interface Region {
  readonly index: number;
  readonly env: BackgroundTheme;
  readonly palette: EnvironmentPalette;
  readonly top: number;
  readonly bottom: number;
  readonly height: number;
  /** Where the near ground begins: the sky is above it. */
  readonly groundY: number;
  readonly rng: Rng;
  readonly trailX: (y: number) => number;
  readonly lastLevel: number;
}

export function drawMapScenery(scene: Phaser.Scene, nodes: readonly MapNode[]): MapScenery {
  const trailX = makeTrailX(nodes);
  const layers: Array<{ top: number; bottom: number; objects: Phaser.GameObjects.GameObject[] }> =
    [];

  const regions = Math.ceil(nodes.length / 10);
  for (let index = 0; index < regions; index += 1) {
    const band = THEME_BANDS[index] ?? THEME_BANDS[0];
    if (band === undefined) continue;
    const span = regionSpan(index, nodes.length);
    const height = span.bottom - span.top;
    const region: Region = {
      index,
      env: band.background,
      palette: paletteFor(band.background),
      top: span.top,
      bottom: span.bottom,
      height,
      groundY: span.top + height * 0.5,
      rng: mulberry32(9001 + index * 131),
      trailX,
      lastLevel: nodes.length,
    };

    const back = scene.add.graphics().setDepth(0);
    drawWorld(back, region);
    // Clouds over the seam with the world above, drawn above both worlds.
    const seam = scene.add.graphics().setDepth(0.5);
    if (index < regions - 1) drawCloudBand(seam, region.top, mulberry32(77 + index));
    layers.push({ top: span.top - 140, bottom: span.bottom + 140, objects: [back, seam] });
  }

  return {
    cull(viewTop: number, viewBottom: number): void {
      for (const layer of layers) {
        const visible = layer.bottom > viewTop - 200 && layer.top < viewBottom + 200;
        for (const object of layer.objects) {
          (object as Gfx).setVisible(visible);
        }
      }
    },
  };
}

/* ---------------------------------------------------------------- worlds */

function drawWorld(g: Gfx, r: Region): void {
  drawSky(g, r);
  switch (r.env) {
    case 'sunset':
      drawSunDisc(g, r, W * 0.78, r.top + r.height * 0.36, 70);
      drawFarRidge(g, r, 0.3);
      drawSea(g, r);
      drawPenguinColony(g, r, 7);
      break;
    case 'midday-glacier':
      drawSunDisc(g, r, W * 0.16, r.top + r.height * 0.18, 46);
      drawFarRidge(g, r, 0.85);
      drawGround(g, r);
      drawCrevasses(g, r, 9);
      drawRouteFlags(g, r, 0xe63946);
      break;
    case 'snowstorm-dusk':
      drawFarRidge(g, r, 0.55);
      drawGround(g, r);
      drawSnowDrifts(g, r);
      drawRouteFlags(g, r, 0xff8c42);
      drawSnowfall(g, r, 140, 0.55);
      break;
    case 'aurora-night':
      drawStars(g, r, 120);
      drawAurora(g, r, [0x6fe3c7, 0x3fbf9f]);
      drawMoon(g, r, W * 0.84, r.top + r.height * 0.14);
      drawFarRidge(g, r, 0.35);
      drawGround(g, r);
      drawTent(g, r);
      break;
    case 'iceberg-sea':
      drawSunDisc(g, r, W * 0.2, r.top + r.height * 0.16, 40);
      drawOpenSea(g, r);
      drawIcebergs(g, r);
      drawWhaleTail(g, r);
      break;
    case 'research-station':
      drawStars(g, r, 50);
      drawFarRidge(g, r, 0.45);
      drawGround(g, r);
      drawStation(g, r);
      break;
    case 'ice-cave':
      drawCave(g, r);
      break;
    case 'blizzard':
      drawFarRidge(g, r, 0.7);
      drawGround(g, r);
      drawRocks(g, r, 7);
      drawRouteFlags(g, r, 0xe63946);
      drawSnowfall(g, r, 260, 0.6, true);
      break;
    case 'midnight-sun':
      drawSunDisc(g, r, W * 0.7, r.top + r.height * 0.42, 84);
      drawFarRidge(g, r, 0.4);
      drawGround(g, r);
      drawPenguinMarch(g, r);
      break;
    case 'grand-aurora':
      drawStars(g, r, 180);
      drawAurora(g, r, [0x7af5d0, 0xc08bff]);
      drawSummit(g, r, summitPoint(r.lastLevel));
      break;
  }
}

/* -------------------------------------------------------------------- sky */

function drawSky(g: Gfx, r: Region): void {
  const step = 8;
  for (let y = r.top; y < r.bottom; y += step) {
    // The world's whole sky runs from the top of its slice down to its ground,
    // so each world shows its own horizon glow just above its land.
    const t = Math.min(1, (y - r.top) / (r.groundY - r.top + r.height * 0.12));
    g.fillStyle(sampleStops(r.palette.sky, t), 1);
    g.fillRect(0, y, W, step + 1);
  }
}

function drawSunDisc(g: Gfx, r: Region, x: number, y: number, radius: number): void {
  const sun = r.palette.sun;
  const color = sun?.color ?? 0xfff3c4;
  const glow = sun?.glow ?? 0xffc145;
  for (let i = 5; i >= 1; i -= 1) {
    g.fillStyle(glow, 0.06);
    g.fillCircle(x, y, radius + i * 22);
  }
  g.fillStyle(color, 1);
  g.fillCircle(x, y, radius);
}

function drawMoon(g: Gfx, r: Region, x: number, y: number): void {
  const color = r.palette.sun?.color ?? 0xf2f6ff;
  softGlow(g, x, y, 80, r.palette.sun?.glow ?? 0x9fd8ff, 0.3);
  g.fillStyle(color, 1);
  g.fillCircle(x, y, 30);
  // A few soft craters.
  g.fillStyle(0x9fb4d8, 0.35);
  g.fillCircle(x - 9, y - 6, 7);
  g.fillCircle(x + 10, y + 8, 5);
  g.fillCircle(x + 6, y - 12, 3.5);
}

function drawStars(g: Gfx, r: Region, count: number): void {
  const skyBottom = r.groundY - 40;
  for (let i = 0; i < count; i += 1) {
    const x = r.rng.next() * W;
    const y = r.top + r.rng.next() * (skyBottom - r.top);
    const big = r.rng.chance(0.12);
    g.fillStyle(0xffffff, 0.35 + r.rng.next() * 0.6);
    g.fillCircle(x, y, big ? 2.2 : 1.2);
    if (big) {
      g.fillStyle(0xffffff, 0.25);
      g.fillRect(x - 6, y - 0.5, 12, 1);
      g.fillRect(x - 0.5, y - 6, 1, 12);
    }
  }
}

function drawAurora(g: Gfx, r: Region, colors: readonly number[]): void {
  // Curtains: many thin vertical strokes along a wavy line, fading upward.
  colors.forEach((color, band) => {
    const baseY = r.top + r.height * (0.18 + band * 0.1);
    const amp = 40 + band * 14;
    const freq = 0.006 + band * 0.002;
    const phase = r.rng.next() * Math.PI * 2;
    for (let x = -10; x < W + 10; x += 6) {
      const y = baseY + Math.sin(x * freq + phase) * amp;
      const tall = 70 + Math.sin(x * freq * 2.3 + phase) * 30;
      for (let k = 0; k < 4; k += 1) {
        g.fillStyle(color, 0.05 + 0.04 * (3 - k));
        g.fillRect(x, y - tall * ((k + 1) / 4), 6, tall / 4 + 1);
      }
    }
  });
}

function drawSnowfall(g: Gfx, r: Region, count: number, alpha: number, streaks = false): void {
  for (let i = 0; i < count; i += 1) {
    const x = r.rng.next() * W;
    const y = r.top + r.rng.next() * r.height;
    if (streaks) {
      g.lineStyle(2, 0xffffff, alpha * (0.3 + r.rng.next() * 0.5));
      g.lineBetween(x, y, x - 34, y + 12);
    } else {
      g.fillStyle(0xffffff, alpha * (0.4 + r.rng.next() * 0.6));
      g.fillCircle(x, y, 1.5 + r.rng.next() * 2);
    }
  }
}

/* ------------------------------------------------------------------- land */

/**
 * A jagged mountain range across the whole width, snow-capped, sitting on the
 * horizon. `lightness` lifts it toward white for the brighter worlds.
 */
function drawFarRidge(g: Gfx, r: Region, lightness: number): void {
  const base = r.groundY + 30;
  const body = mix(r.palette.far, 0xffffff, lightness * 0.35);
  const points: Array<{ x: number; y: number }> = [];
  let x = -40;
  while (x < W + 60) {
    const peak = r.rng.chance(0.5);
    const h = peak ? 120 + r.rng.next() * 170 : 40 + r.rng.next() * 60;
    points.push({ x, y: base - h });
    x += 70 + r.rng.next() * 90;
  }
  g.fillStyle(body, 1);
  g.beginPath();
  g.moveTo(-40, base + 40);
  for (const p of points) g.lineTo(p.x, p.y);
  g.lineTo(W + 60, base + 40);
  g.closePath();
  g.fillPath();

  // Snow caps on the taller peaks.
  const snow = mix(0xffffff, r.palette.farAccent, 0.15);
  for (let i = 1; i < points.length - 1; i += 1) {
    const p = points[i];
    const prev = points[i - 1];
    const next = points[i + 1];
    if (p === undefined || prev === undefined || next === undefined) continue;
    if (base - p.y < 150) continue;
    const drop = 0.32;
    const lx = p.x + (prev.x - p.x) * drop;
    const ly = p.y + (prev.y - p.y) * drop;
    const rx = p.x + (next.x - p.x) * drop;
    const ry = p.y + (next.y - p.y) * drop;
    g.fillStyle(snow, 0.95);
    g.beginPath();
    g.moveTo(p.x, p.y);
    g.lineTo(rx, ry);
    g.lineTo((p.x + rx) / 2, Math.max(ly, ry) + 10);
    g.lineTo(p.x, Math.max(ly, ry) - 4);
    g.lineTo((p.x + lx) / 2, Math.max(ly, ry) + 12);
    g.lineTo(lx, ly);
    g.closePath();
    g.fillPath();
  }
}

/** Rolling snowfields from the horizon down to the bottom of the world. */
function drawGround(g: Gfx, r: Region): void {
  const near = r.palette.near;
  const rows = 3;
  for (let row = 0; row < rows; row += 1) {
    const top = r.groundY + 40 + row * 70;
    const color = mix(near, 0xffffff, 0.12 * (rows - 1 - row));
    g.fillStyle(color, 1);
    g.beginPath();
    g.moveTo(0, r.bottom);
    const phase = r.rng.next() * Math.PI * 2;
    for (let x = 0; x <= W; x += 20) {
      g.lineTo(x, top + Math.sin(x * 0.006 + phase) * 22 + Math.sin(x * 0.017 + phase) * 8);
    }
    g.lineTo(W, r.bottom);
    g.closePath();
    g.fillPath();
  }
  // A soft rim of light along the nearest drift.
  g.lineStyle(3, r.palette.nearAccent, 0.35);
  g.beginPath();
  const top = r.groundY + 40;
  for (let x = 0; x <= W; x += 20) {
    const y = top + 8;
    if (x === 0) g.moveTo(x, y);
    else g.lineTo(x, y + Math.sin(x * 0.01) * 4);
  }
  g.strokePath();
}

function drawCrevasses(g: Gfx, r: Region, count: number): void {
  for (let i = 0; i < count; i += 1) {
    const spot = sideSpot(r, 30, 60);
    if (spot === null) continue;
    const len = 50 + r.rng.next() * 70;
    g.fillStyle(0x3a7fb3, 0.75);
    g.beginPath();
    g.moveTo(spot.x - len / 2, spot.y);
    g.lineTo(spot.x - len / 6, spot.y - 5);
    g.lineTo(spot.x + len / 2, spot.y - 1);
    g.lineTo(spot.x + len / 5, spot.y + 4);
    g.closePath();
    g.fillPath();
  }
}

function drawSnowDrifts(g: Gfx, r: Region): void {
  for (let i = 0; i < 10; i += 1) {
    const spot = sideSpot(r, 40, 60);
    if (spot === null) continue;
    g.fillStyle(0xffffff, 0.18);
    g.fillEllipse(spot.x, spot.y, 120 + r.rng.next() * 80, 18);
  }
}

function drawRocks(g: Gfx, r: Region, count: number): void {
  for (let i = 0; i < count; i += 1) {
    const spot = sideSpot(r, 50, 60);
    if (spot === null) continue;
    const s = 18 + r.rng.next() * 22;
    g.fillStyle(0x4a5568, 1);
    g.beginPath();
    g.moveTo(spot.x - s, spot.y);
    g.lineTo(spot.x - s * 0.5, spot.y - s * 0.8);
    g.lineTo(spot.x + s * 0.4, spot.y - s);
    g.lineTo(spot.x + s, spot.y);
    g.closePath();
    g.fillPath();
    g.fillStyle(0xffffff, 0.9);
    g.fillEllipse(spot.x - s * 0.05, spot.y - s * 0.82, s * 0.9, s * 0.3);
  }
}

function drawRouteFlags(g: Gfx, r: Region, color: number): void {
  // Expedition route markers: little flags on poles beside the trail.
  for (let i = 0; i < 6; i += 1) {
    const y = r.groundY + 80 + (i / 6) * (r.bottom - r.groundY - 120);
    const side = i % 2 === 0 ? -1 : 1;
    const x = r.trailX(y) + side * (70 + r.rng.next() * 20);
    flag(g, x, y, color, 0.8);
  }
}

/* ------------------------------------------------------------- creatures */

function penguin(g: Gfx, x: number, y: number, s: number, flip = 1): void {
  g.fillStyle(0x1f2733, 1);
  g.fillEllipse(x, y - 12 * s, 16 * s, 24 * s);
  g.fillCircle(x, y - 26 * s, 6.5 * s);
  g.fillStyle(0xf8faff, 1);
  g.fillEllipse(x + 1 * s * flip, y - 10 * s, 9 * s, 16 * s);
  g.fillStyle(0xf59a5b, 1);
  g.fillTriangle(
    x + 4 * s * flip,
    y - 27 * s,
    x + 10 * s * flip,
    y - 25.5 * s,
    x + 4 * s * flip,
    y - 24 * s,
  );
  g.fillStyle(0xffd166, 0.9);
  g.fillCircle(x + 1 * s * flip, y - 23 * s, 1.8 * s);
}

function drawPenguinColony(g: Gfx, r: Region, count: number): void {
  const y0 = r.bottom - 150;
  for (let i = 0; i < count; i += 1) {
    const x = 70 + i * 34 + r.rng.next() * 12;
    const y = y0 + (i % 2) * 14 + r.rng.next() * 6;
    penguin(g, x, y, 1.1 + r.rng.next() * 0.3, i % 3 === 0 ? -1 : 1);
  }
}

function drawPenguinMarch(g: Gfx, r: Region): void {
  // Emperors marching single file across the snow in the low sun.
  const y = r.groundY + 150;
  for (let i = 0; i < 9; i += 1) {
    const x = 30 + i * 30;
    penguin(g, x, y + Math.sin(i) * 3, 0.95, 1);
  }
}

function drawWhaleTail(g: Gfx, r: Region): void {
  // Out in open water, between the bergs and away from the trail.
  const y = r.groundY + 230;
  const x = Math.abs(W - 330 - r.trailX(y)) > TRAIL_CLEARANCE ? W - 330 : 330;
  const tail = 0x1b3a52;
  g.fillStyle(tail, 1);
  g.fillTriangle(x - 8, y, x + 8, y, x, y - 40);
  g.fillEllipse(x - 26, y - 46, 56, 20);
  g.fillEllipse(x + 26, y - 46, 56, 20);
  g.fillStyle(0xffffff, 0.6);
  for (let i = 0; i < 5; i += 1) {
    g.fillCircle(x - 30 + i * 15, y + 2, 3);
  }
}

/* ------------------------------------------------------------ the seas */

function drawSea(g: Gfx, r: Region): void {
  // The shore the climb starts from: open water below the first level.
  const seaTop = r.bottom - 120;
  drawGround(g, { ...r, bottom: seaTop + 20 });
  g.fillStyle(0x16324f, 1);
  g.fillRect(0, seaTop, W, r.bottom - seaTop);
  g.fillStyle(0xff7b54, 0.25);
  g.fillRect(0, seaTop, W, 10);
  for (let i = 0; i < 26; i += 1) {
    const x = r.rng.next() * W;
    const y = seaTop + 16 + r.rng.next() * (r.bottom - seaTop - 20);
    g.lineStyle(2, 0xffc145, 0.25 + r.rng.next() * 0.25);
    g.lineBetween(x, y, x + 18 + r.rng.next() * 30, y);
  }
  for (let i = 0; i < 5; i += 1) {
    const x = 60 + i * 260 + r.rng.next() * 60;
    const y = seaTop + 40 + r.rng.next() * 40;
    floe(g, x, y, 60 + r.rng.next() * 40);
  }
}

function drawOpenSea(g: Gfx, r: Region): void {
  const seaTop = r.groundY + 30;
  for (let y = seaTop; y < r.bottom; y += 8) {
    const t = (y - seaTop) / (r.bottom - seaTop);
    g.fillStyle(mix(0x1f6f9c, 0x0a2d49, t), 1);
    g.fillRect(0, y, W, 9);
  }
  for (let i = 0; i < 60; i += 1) {
    const x = r.rng.next() * W;
    const y = seaTop + 10 + r.rng.next() * (r.bottom - seaTop - 20);
    g.lineStyle(2, 0xa9dff0, 0.25);
    g.lineBetween(x, y, x + 14 + r.rng.next() * 24, y);
  }
}

function floe(g: Gfx, x: number, y: number, w: number): void {
  g.fillStyle(0x7fb6d6, 0.5);
  g.fillEllipse(x, y + 6, w + 8, w * 0.28);
  g.fillStyle(0xeef7ff, 1);
  g.fillEllipse(x, y, w, w * 0.26);
  g.fillStyle(0xffffff, 1);
  g.fillEllipse(x - w * 0.12, y - 2, w * 0.5, w * 0.1);
}

function drawIcebergs(g: Gfx, r: Region): void {
  const seaTop = r.groundY + 30;
  const spots = [
    { x: 150, y: seaTop + 40, s: 1.25 },
    { x: W - 150, y: seaTop + 70, s: 1.0 },
    { x: 110, y: seaTop + 330, s: 0.8 },
    { x: W - 120, y: seaTop + 420, s: 0.9 },
  ];
  for (const { x, y, s } of spots) {
    // Below the waterline, a paler ghost of the berg.
    g.fillStyle(0x8fd6ef, 0.3);
    g.beginPath();
    g.moveTo(x - 92 * s, y);
    g.lineTo(x + 96 * s, y);
    g.lineTo(x + 70 * s, y + 60 * s);
    g.lineTo(x + 10 * s, y + 92 * s);
    g.lineTo(x - 55 * s, y + 70 * s);
    g.closePath();
    g.fillPath();
    g.fillStyle(0xf2fbff, 1);
    g.beginPath();
    g.moveTo(x - 80 * s, y);
    g.lineTo(x - 50 * s, y - 60 * s);
    g.lineTo(x - 10 * s, y - 70 * s);
    g.lineTo(x + 20 * s, y - 110 * s);
    g.lineTo(x + 60 * s, y - 50 * s);
    g.lineTo(x + 85 * s, y);
    g.closePath();
    g.fillPath();
    g.fillStyle(0x9fd3ea, 1);
    g.beginPath();
    g.moveTo(x + 20 * s, y - 110 * s);
    g.lineTo(x + 60 * s, y - 50 * s);
    g.lineTo(x + 85 * s, y);
    g.lineTo(x + 25 * s, y);
    g.closePath();
    g.fillPath();
  }
  // A seal napping on a floe.
  floe(g, W - 260, seaTop + 250, 110);
  g.fillStyle(0x6e7a8a, 1);
  g.fillEllipse(W - 262, seaTop + 238, 56, 20);
  g.fillCircle(W - 238, seaTop + 233, 10);
}

/* ----------------------------------------------------------- the station */

function drawStation(g: Gfx, r: Region): void {
  const ground = r.groundY + 120;
  hut(g, 120, ground, 120, 60, 0xff8c42);
  hut(g, 250, ground + 60, 90, 48, 0xe63946);
  hut(g, W - 170, ground + 20, 130, 64, 0xff8c42);
  // Radio mast with a red light.
  const mx = W - 300;
  g.lineStyle(4, 0xb8c6d9, 1);
  g.lineBetween(mx, ground + 30, mx, ground - 150);
  g.lineStyle(2, 0xb8c6d9, 0.8);
  for (let i = 0; i < 5; i += 1) {
    const y = ground + 30 - i * 36;
    g.lineBetween(mx - 10, y, mx + 10, y - 36);
  }
  g.fillStyle(0xff4d4d, 1);
  g.fillCircle(mx, ground - 152, 5);
  g.fillStyle(0xff4d4d, 0.25);
  g.fillCircle(mx, ground - 152, 14);
  flag(g, 360, ground + 10, 0x2ec4b6, 1.1);
}

function hut(g: Gfx, x: number, y: number, w: number, h: number, color: number): void {
  // On stilts, so the snow drifts underneath instead of burying it.
  g.fillStyle(0x33425c, 1);
  for (const sx of [x - w * 0.38, x, x + w * 0.38]) g.fillRect(sx - 3, y, 6, 22);
  g.fillStyle(color, 1);
  g.fillRoundedRect(x - w / 2, y - h, w, h, 8);
  g.fillStyle(0xffffff, 0.9);
  g.fillRoundedRect(x - w / 2 - 4, y - h - 8, w + 8, 12, 6);
  g.fillStyle(0xffe08a, 1);
  const windows = Math.max(1, Math.floor(w / 40));
  for (let i = 0; i < windows; i += 1) {
    const wx = x - w / 2 + ((i + 0.5) * w) / windows;
    g.fillRoundedRect(wx - 8, y - h * 0.65, 16, 14, 3);
  }
}

function drawTent(g: Gfx, r: Region): void {
  const x = 170;
  const y = r.groundY + 150;
  softGlow(g, x, y - 20, 70, 0xffc145, 0.2);
  g.fillStyle(0xff8c42, 1);
  g.fillTriangle(x - 50, y, x + 50, y, x, y - 60);
  g.fillStyle(0x7a2e12, 1);
  g.fillTriangle(x - 12, y, x + 12, y, x, y - 34);
  g.fillStyle(0xffe08a, 0.8);
  g.fillTriangle(x - 8, y, x + 8, y, x, y - 24);
}

/* ---------------------------------------------------------------- the cave */

function drawCave(g: Gfx, r: Region): void {
  // Cave walls close in from both sides, hung with icicles, lit by crystals.
  const wall = r.palette.near;
  for (const side of [-1, 1]) {
    g.fillStyle(wall, 1);
    g.beginPath();
    const edge = side < 0 ? 0 : W;
    g.moveTo(edge, r.top);
    for (let y = r.top; y <= r.bottom; y += 40) {
      const depth = 170 + Math.sin(y * 0.01 + side) * 60 + r.rng.next() * 30;
      g.lineTo(edge - side * depth, y);
    }
    g.lineTo(edge, r.bottom);
    g.closePath();
    g.fillPath();
  }
  // Icicles along the top of the slice.
  g.fillStyle(0xbfeeff, 0.85);
  for (let x = 0; x < W; x += 26 + r.rng.next() * 20) {
    const len = 20 + r.rng.next() * 60;
    g.fillTriangle(x, r.top, x + 14, r.top, x + 7, r.top + len);
  }
  for (let i = 0; i < 12; i += 1) {
    const left = i % 2 === 0;
    const x = left ? 40 + r.rng.next() * 140 : W - 40 - r.rng.next() * 140;
    const y = r.top + 100 + r.rng.next() * (r.height - 160);
    crystal(g, x, y, 0.7 + r.rng.next() * 0.7, r.palette.nearAccent);
  }
}

function crystal(g: Gfx, x: number, y: number, s: number, color: number): void {
  softGlow(g, x, y - 22 * s, 46 * s, color, 0.22);
  for (const [dx, h, w] of [
    [-10, 36, 9],
    [0, 52, 11],
    [11, 30, 8],
  ] as const) {
    g.fillStyle(color, 0.9);
    g.fillTriangle(x + (dx - w) * s, y, x + (dx + w) * s, y, x + dx * s, y - h * s);
    g.fillStyle(0xffffff, 0.5);
    g.fillTriangle(x + dx * s, y, x + (dx + w * 0.6) * s, y, x + dx * s, y - h * s);
  }
}

/* --------------------------------------------------------------- the top */

function drawSummit(g: Gfx, r: Region, apex: { x: number; y: number }): void {
  // One great peak whose tip is level 100, with the ceremonial South Pole on it.
  const peakX = apex.x;
  const peakY = apex.y;
  g.fillStyle(mix(r.palette.far, 0x9fb4d8, 0.28), 1);
  g.beginPath();
  g.moveTo(-60, r.bottom);
  g.lineTo(peakX - 420, peakY + 560);
  g.lineTo(peakX, peakY);
  g.lineTo(peakX + 440, peakY + 600);
  g.lineTo(W + 60, r.bottom);
  g.closePath();
  g.fillPath();
  g.fillStyle(0xeaf4ff, 1);
  g.beginPath();
  g.moveTo(peakX, peakY);
  g.lineTo(peakX + 120, peakY + 170);
  g.lineTo(peakX + 50, peakY + 140);
  g.lineTo(peakX, peakY + 190);
  g.lineTo(peakX - 60, peakY + 140);
  g.lineTo(peakX - 112, peakY + 170);
  g.closePath();
  g.fillPath();
  g.fillStyle(0x000000, 0.18);
  g.beginPath();
  g.moveTo(peakX, peakY);
  g.lineTo(peakX - 420, peakY + 560);
  g.lineTo(-60, r.bottom);
  g.lineTo(peakX - 40, r.bottom);
  g.closePath();
  g.fillPath();
}

/** The ceremonial South Pole: a striped pole topped with a mirrored ball. */
export function drawSouthPole(g: Gfx, x: number, y: number): void {
  // A ring of flags planted down the slopes either side of the tip.
  const flags = [0xe63946, 0x2ec4b6, 0xffc145, 0x4ea8de, 0xc08bff, 0xff8c42];
  flags.forEach((color, i) => {
    const dx = (i < 3 ? -1 : 1) * (40 + (i % 3) * 34);
    flag(g, x + dx, y + Math.abs(dx) * 1.3 + 4, color, 0.8);
  });
  const h = 120;
  for (let i = 0; i < 8; i += 1) {
    g.fillStyle(i % 2 === 0 ? 0xe63946 : 0xffffff, 1);
    g.fillRect(x - 6, y - h + i * (h / 8), 12, h / 8 + 1);
  }
  g.fillStyle(0xd7e3f0, 1);
  g.fillCircle(x, y - h - 14, 18);
  g.fillStyle(0xffffff, 0.9);
  g.fillCircle(x - 6, y - h - 20, 6);
  g.fillStyle(0x7e8fa6, 0.6);
  g.fillCircle(x + 6, y - h - 8, 6);
}

/* ---------------------------------------------------------------- clouds */

function drawCloudBand(g: Gfx, y: number, rng: Rng): void {
  for (let i = 0; i < 26; i += 1) {
    const x = (i / 25) * (W + 200) - 100 + rng.next() * 40;
    const w = 180 + rng.next() * 160;
    const h = 60 + rng.next() * 50;
    const dy = (rng.next() - 0.5) * 70;
    g.fillStyle(0xffffff, 0.16);
    g.fillEllipse(x, y + dy, w * 1.3, h * 1.4);
    g.fillStyle(0xffffff, 0.32);
    g.fillEllipse(x, y + dy, w, h);
  }
}

/* --------------------------------------------------------------- helpers */

/** Light that fades out from its centre, instead of one flat disc. */
function softGlow(
  g: Gfx,
  x: number,
  y: number,
  radius: number,
  color: number,
  alpha: number,
): void {
  const rings = 6;
  for (let i = rings; i >= 1; i -= 1) {
    g.fillStyle(color, alpha / rings);
    g.fillCircle(x, y, (radius * i) / rings);
  }
}

function flag(g: Gfx, x: number, y: number, color: number, s: number): void {
  g.lineStyle(3 * s, 0x2b3a52, 1);
  g.lineBetween(x, y, x, y - 46 * s);
  g.fillStyle(color, 1);
  g.fillTriangle(x, y - 46 * s, x + 26 * s, y - 38 * s, x, y - 30 * s);
}

/** A spot on the ground of this world, clear of the trail, or null if none. */
function sideSpot(
  r: Region,
  marginTop: number,
  marginBottom: number,
): { x: number; y: number } | null {
  for (let tries = 0; tries < 12; tries += 1) {
    const y =
      r.groundY + marginTop + r.rng.next() * (r.bottom - r.groundY - marginTop - marginBottom);
    const x = 30 + r.rng.next() * (W - 60);
    if (Math.abs(x - r.trailX(y)) > TRAIL_CLEARANCE) return { x, y };
  }
  return null;
}

/** The trail's x at any height, by straight interpolation between nodes. */
function makeTrailX(nodes: readonly MapNode[]): (y: number) => number {
  return (y: number): number => {
    for (let i = 1; i < nodes.length; i += 1) {
      const a = nodes[i - 1];
      const b = nodes[i];
      if (a === undefined || b === undefined) continue;
      if (y <= a.y && y >= b.y) {
        const t = (a.y - y) / (a.y - b.y);
        return a.x + (b.x - a.x) * t;
      }
    }
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (first !== undefined && y > first.y) return first.x;
    return last?.x ?? W / 2;
  };
}

function mix(a: number, b: number, t: number): number {
  const c = Phaser.Display.Color.Interpolate.ColorWithColor(
    Phaser.Display.Color.ValueToColor(a),
    Phaser.Display.Color.ValueToColor(b),
    100,
    Math.round(Math.max(0, Math.min(1, t)) * 100),
  );
  return Phaser.Display.Color.GetColor(c.r, c.g, c.b);
}

function sampleStops(stops: GradientStops, t: number): number {
  const first = stops[0];
  if (first === undefined) return 0x000000;
  if (t <= first[1]) return first[0];
  for (let i = 1; i < stops.length; i += 1) {
    const previous = stops[i - 1];
    const current = stops[i];
    if (previous === undefined || current === undefined) continue;
    if (t <= current[1]) {
      const span = current[1] - previous[1];
      return mix(previous[0], current[0], span <= 0 ? 0 : (t - previous[1]) / span);
    }
  }
  return stops[stops.length - 1]?.[0] ?? 0x000000;
}
